import { useQuery } from "@tanstack/react-query";
import { useAdapter, useTopology } from "@/store/site";
import type { Alarm } from "@/data/types";

/**
 * Single shared alarms source for the whole SPA.
 *
 * Previously HealthBanner (5 s), AlarmsCompact (4 s) and useAlarmWatcher
 * (6 s) EACH ran their own `setInterval` + `adapter.alarms()` fetch — three
 * overlapping REST round-trips whose synchronous response-parsing landed on
 * the main thread on near-aligned phases. Combined with the 5 s
 * ModeReasoningCard schedule poll (which mounts in the same commit as
 * HealthBanner, so the two 5 s timers fire on the SAME frame), this produced
 * the Overview's "stuck moment every 5 seconds": a periodic main-thread hit
 * that drops a frame and freezes the (main-thread) SCADA SMIL/CSS animations
 * and interrupts hover/`title` tooltips.
 *
 * Consolidating to one React-Query cache means ONE fetch + ONE parse, shared
 * by every consumer via the query key. The interval is also relaxed to 15 s
 * (an Overview doesn't need 4–5 s alarm refresh) and intentionally NOT a
 * round multiple of the schedule poll (20 s), so the two rarely coincide.
 */
export function useAlarms() {
  const adapter = useAdapter();
  const topology = useTopology();
  return useQuery<Alarm[]>({
    // Site id in the key so a live-mode site swap refetches into a fresh cache.
    queryKey: ["alarms", topology.id],
    queryFn: () => adapter.alarms(),
    refetchInterval: 15_000,
    staleTime: 10_000,
    // Background-tab alarm signal (XTRA-02): control-room reality is this
    // console sitting in a hidden tab next to TB/SCADA/email. TanStack v5
    // pauses interval refetches on hidden tabs by default, which would mute
    // the title/favicon badge and the critical-alarm toasts exactly when
    // they matter — keep polling while hidden, and do one catch-up fetch on
    // refocus. Both are deliberate PER-QUERY overrides of the global
    // `refetchOnWindowFocus: false` default in src/main.tsx (v5 merges
    // query options over defaultOptions, so this query wins).
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
}
